import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { MessageSquare, Users, Lock } from 'lucide-react';
import {
  useRotationHealth,
  HEALTH_RING_CLASS,
} from '../../hooks/useRotationHealth';
import { CadenceBadge } from '../CadenceBadge';

/**
 * One conversation row. Extracted so we can call `useRotationHealth`
 * at component top-level — hooks can't live inside a `.map()`.
 *
 * The health ring is a soft, ambient trust signal: healthy peers
 * (cadence ≤10d) wear an emerald 2px halo, slipping peers amber,
 * inactive / sleeper peers rose. Null state = no ring, so the
 * visual only appears when we actually have data.
 */
const ConversationRow = ({
  conv, active, onSelect, contacts, userId, getConversationName, getContactStatus,
}) => {
  const otherId = conv.type === 'group'
    ? null
    : conv.participants.find((p) => p !== userId);
  const health = useRotationHealth(otherId);
  const ringClass = health ? HEALTH_RING_CLASS[health] : '';

  return (
    <button
      onClick={() => onSelect(conv)}
      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
        active
          ? 'bg-primary/10 border border-primary/30'
          : 'hover:bg-secondary/50 border border-transparent'
      }`}
      data-testid={`conversation-${conv.id}`}
    >
      <div className="relative">
        <Avatar
          className={`h-11 w-11 ${ringClass} ring-offset-2 ring-offset-card`}
          data-testid={health ? `avatar-health-${health}` : undefined}
        >
          <AvatarFallback className="bg-secondary text-slate-300">
            {conv.type === 'group' ? (
              <Users className="w-5 h-5" />
            ) : (
              getConversationName(conv)[0]?.toUpperCase()
            )}
          </AvatarFallback>
        </Avatar>
        {conv.type !== 'group' && (
          <span
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-card ${
              getContactStatus(conv) ? 'bg-emerald-500' : 'bg-slate-500'
            }`}
          />
        )}
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-sm font-medium text-white truncate flex items-center gap-1.5">
          <span className="truncate">{getConversationName(conv)}</span>
          {otherId && <CadenceBadge userId={otherId} />}
        </p>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Lock className="w-3 h-3" />
          <span>Encrypted</span>
        </div>
      </div>
    </button>
  );
};

export const ConversationList = ({ conversations, activeConvId, onSelect, contacts }) => {
  const { user } = useAuth();

  const getConversationName = (conv) => {
    if (conv.type === 'group') {
      return conv.encrypted_name || 'Group Chat';
    }
    const otherId = conv.participants.find((p) => p !== user.id);
    const contact = contacts.find((c) => c.id === otherId);
    return contact?.username || 'Unknown';
  };

  const getContactStatus = (conv) => {
    if (conv.type === 'group') return null;
    const otherId = conv.participants.find((p) => p !== user.id);
    const contact = contacts.find((c) => c.id === otherId);
    return contact?.is_online;
  };

  return (
    <div className="flex-1 overflow-hidden">
      <ScrollArea className="h-full">
        <div className="p-3 space-y-1">
          {conversations.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No conversations yet</p>
              <p className="text-sm mt-1">Start a new chat to begin</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <ConversationRow
                key={conv.id}
                conv={conv}
                active={activeConvId === conv.id}
                onSelect={onSelect}
                contacts={contacts}
                userId={user.id}
                getConversationName={getConversationName}
                getContactStatus={getContactStatus}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
